class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // FF mobile: preserve active filters when the separate Sort dropdown changes.
    const submittedForm = event.target.closest('form');
    if (submittedForm?.id === 'FacetSortMobileDropdownForm') {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', event.target.value);
      this.onSubmitForm(params.toString(), event);
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* Flourishing Fragrances | Mobile custom Sort menu, preserving Shopify facets */
(() => {
  const rootSelector = '#FacetSortMobileDropdownForm .ff-sort-details';

  function refreshMenu(select) {
    if (!select) return;
    const details = select.closest('.mobile-sort-dropdown__field')?.querySelector('.ff-sort-details');
    const selected = select.selectedOptions[0];
    if (!details || !selected) return;
    const visibleValue = details.querySelector('.ff-sort-trigger__value');
    if (visibleValue) visibleValue.textContent = selected.textContent.trim();
    details.querySelectorAll('.ff-sort-option').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sortValue === select.value));
    });
  }

  function synchronizeShopifySort(value) {
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (Array.from(select.options).some((option) => option.value === value)) select.value = value;
    });
  }

  document.addEventListener('click', (event) => {
    const option = event.target.closest('.ff-sort-option[data-sort-value]');
    if (option) {
      const details = option.closest('.ff-sort-details');
      const select = details?.closest('.mobile-sort-dropdown__field')?.querySelector('#SortByMobileDropdown');
      if (!select) return;
      const changed = select.value !== option.dataset.sortValue;
      synchronizeShopifySort(option.dataset.sortValue);
      refreshMenu(select);
      details.open = false;
      if (changed) select.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    document.querySelectorAll(`${rootSelector}[open]`).forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(`${rootSelector}[open]`).forEach((details) => {
      details.open = false;
      details.querySelector('summary')?.focus();
    });
  });

  window.addEventListener('popstate', () => {
    const select = document.querySelector('#FacetSortMobileDropdownForm #SortByMobileDropdown');
    if (!select) return;
    const value = new URLSearchParams(window.location.search).get('sort_by');
    const fallback = Array.from(select.options).find((option) => option.defaultSelected)?.value || select.options[0]?.value;
    if (Array.from(select.options).some((option) => option.value === (value || fallback))) {
      synchronizeShopifySort(value || fallback);
      refreshMenu(select);
    }
  });
})();
/* Flourishing Fragrances | persistent, moving mobile Sort scroll indicator
   Append to END of Assets/facets.js. Does not modify Shopify's sort logic. */
(() => {
  const selector = '.ff-sort-details';

  function renderIndicator(details) {
    if (!details || !details.open || window.innerWidth >= 750) return;
    const menu = details.querySelector('.ff-sort-menu');
    if (!menu) return;

    let track = details.querySelector('.ff-sort-scroll-track');
    if (!track) {
      track = document.createElement('span');
      track.className = 'ff-sort-scroll-track';
      track.setAttribute('aria-hidden', 'true');
      const thumb = document.createElement('span');
      thumb.className = 'ff-sort-scroll-thumb';
      track.appendChild(thumb);
      details.appendChild(track);
    }

    const overflowing = menu.scrollHeight - menu.clientHeight > 3;
    track.hidden = !overflowing;
    if (!overflowing) return;

    const detailsRect = details.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const height = Math.max(24, menu.clientHeight - 20);
    track.style.top = `${menuRect.top - detailsRect.top + 10}px`;
    track.style.height = `${height}px`;

    const thumb = track.firstElementChild;
    const thumbHeight = Math.max(18, (menu.clientHeight / menu.scrollHeight) * height);
    thumb.style.height = `${thumbHeight}px`;
    const maxScroll = menu.scrollHeight - menu.clientHeight;
    const thumbTravel = height - thumbHeight;
    thumb.style.transform = `translateY(${Math.max(0, Math.min(1, menu.scrollTop / maxScroll)) * thumbTravel}px)`;
  }

  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.(selector)) {
      requestAnimationFrame(() => renderIndicator(event.target));
    }
  }, true);

  document.addEventListener('scroll', (event) => {
    if (event.target.matches?.('.ff-sort-menu')) {
      renderIndicator(event.target.closest(selector));
    }
  }, true);

  document.addEventListener('click', (event) => {
    const details = event.target.closest(selector);
    if (details) requestAnimationFrame(() => renderIndicator(details));
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(`${selector}[open]`).forEach(renderIndicator);
  });
})();
/* FLOURISHING FRAGRANCES | Desktop custom Sort By
   Append to the end of Assets/facets.js. Mobile Sort remains unchanged.
   Enhances Shopify's existing desktop sort select and uses its input event. */
(() => {
  const selectQuery = [
    '#FacetFiltersForm', '#FacetSortForm', '#FacetSortDrawerForm'
  ].map((id) => `${id} .facet-filters.sorting select[name="sort_by"]`).join(', ');

  function syncButtons() {
    document.querySelectorAll('.ff-desktop-sort-details').forEach((details) => {
      const select = details.parentElement.querySelector('select[name="sort_by"]');
      if (!select) return;
      const chosen = select.selectedOptions[0];
      const valueEl = details.querySelector('.ff-desktop-sort-value');
      if (chosen && valueEl) valueEl.textContent = chosen.textContent.trim();
      details.querySelectorAll('.ff-desktop-sort-option').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.sortValue === select.value));
      });
    });
  }

  function updateScrollIndicator(details) {
    if (!details?.open) return;
    const menu = details.querySelector('.ff-desktop-sort-menu');
    const track = details.querySelector('.ff-desktop-sort-scroll-track');
    const thumb = track?.firstElementChild;
    if (!menu || !track || !thumb) return;
    const overflow = menu.scrollHeight - menu.clientHeight;
    track.hidden = overflow < 3;
    if (track.hidden) return;
    const trackHeight = Math.max(30, menu.clientHeight - 16);
    const thumbHeight = Math.max(24, trackHeight * menu.clientHeight / menu.scrollHeight);
    track.style.top = `${menu.offsetTop + 8}px`;
    track.style.height = `${trackHeight}px`;
    thumb.style.height = `${thumbHeight}px`;
    
const safeScrollProgress = Math.max(
  0,
  Math.min(1, menu.scrollTop / overflow)
);

thumb.style.transform = `translateY(${
  safeScrollProgress *
  Math.max(0, trackHeight - thumbHeight)
}px)`;

  }

  function enhance() {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    document.querySelectorAll(selectQuery).forEach((select) => {
      const wrapper = select.closest('.select');
      if (!wrapper || wrapper.querySelector('.ff-desktop-sort-details')) return;
      wrapper.classList.add('ff-desktop-sort-ready');
      select.classList.add('ff-desktop-sort-native');

      const details = document.createElement('details');
      details.className = 'ff-desktop-sort-details';
      const summary = document.createElement('summary');
      summary.className = 'ff-desktop-sort-trigger';
      summary.setAttribute('aria-label', 'Sort products');
      const heading = document.createElement('span');
      heading.className = 'ff-desktop-sort-heading';
      heading.textContent = 'SORT BY';
      const line = document.createElement('span');
      line.className = 'ff-desktop-sort-row';
      const current = document.createElement('span');
      current.className = 'ff-desktop-sort-value';
      const arrow = document.createElement('span');
      arrow.className = 'ff-desktop-sort-chevron';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.innerHTML = '<svg viewBox="0 0 16 10" width="16" height="10" focusable="false" aria-hidden="true"><path d="m2 2 6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      line.append(current, arrow);
      summary.append(heading, line);

      const menu = document.createElement('div');
      menu.className = 'ff-desktop-sort-menu';
      menu.setAttribute('aria-label', 'Sort options');
      Array.from(select.options).forEach((nativeOption) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ff-desktop-sort-option';
        button.dataset.sortValue = nativeOption.value;
        const label = document.createElement('span');
        label.textContent = nativeOption.textContent.trim();
        const check = document.createElement('span');
        check.className = 'ff-desktop-sort-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        button.append(label, check);
        menu.appendChild(button);
      });

      const track = document.createElement('span');
      track.className = 'ff-desktop-sort-scroll-track';
      track.hidden = true;
      track.setAttribute('aria-hidden', 'true');
      const thumb = document.createElement('span');
      thumb.className = 'ff-desktop-sort-scroll-thumb';
      track.appendChild(thumb);
      details.append(summary, menu, track);
      wrapper.appendChild(details);
    });
    syncButtons();
  }

  document.addEventListener('click', (event) => {
    const choice = event.target.closest('.ff-desktop-sort-option');
    if (choice) {
      const details = choice.closest('.ff-desktop-sort-details');
      const select = details?.parentElement.querySelector('select[name="sort_by"]');
      if (!select) return;
      const changed = choice.dataset.sortValue !== select.value;
      document.querySelectorAll('select[name="sort_by"]').forEach((other) => {
        if (Array.from(other.options).some((option) => option.value === choice.dataset.sortValue)) {
          other.value = choice.dataset.sortValue;
        }
      });
      syncButtons();
      details.open = false;
      if (changed) select.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    document.querySelectorAll('.ff-desktop-sort-details[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    const details = event.target.closest('.ff-desktop-sort-details');
    if (!details) return;
    if (event.key === 'Escape' && details.open) {
      event.preventDefault();
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!details.matches?.('.ff-desktop-sort-details') || !details.open) return;
    document.querySelectorAll('.ff-desktop-sort-details[open]').forEach((other) => {
      if (other !== details) other.open = false;
    });
    requestAnimationFrame(() => updateScrollIndicator(details));
  }, true);

  document.addEventListener('scroll', (event) => {
    if (event.target.matches?.('.ff-desktop-sort-menu')) {
      updateScrollIndicator(event.target.closest('.ff-desktop-sort-details'));
    }
  }, true);

  // Shopify redraws the Sort field after filtering; enhance the new select again.
  function start() {
    enhance();
    document.querySelectorAll('#MainContent .facet-filters.sorting').forEach((sorting) => {
      const observer = new MutationObserver(() => enhance());
      observer.observe(sorting, { childList: true, subtree: false });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('resize', enhance);
})();
/* Flourishing Fragrances | Instant mobile filter submenu navigation.
   Append to assets/facets.js. Scope is restricted to the mobile
   Availability / Price submenus; Shopify's other drawers are untouched. */
(() => {
  const scope = '#MainContent .mobile-facets__wrapper .mobile-facets__main';

  function closeSubmenu(details, restoreFocus = true) {
    if (!details) return;
    const summary = details.querySelector(':scope > .mobile-facets__summary');
    const main = details.closest('.mobile-facets__main');
    details.classList.remove('menu-opening');
    details.open = false;
    summary?.setAttribute('aria-expanded', 'false');
    main?.classList.remove('submenu-open');

    if (restoreFocus && summary) {
      // Shopify's normal drawer restores the parent focus trap after
      // its 400ms animation; there is no animation in this custom popup.
      const outer = details.closest('.mobile-facets__disclosure');
      if (outer && typeof trapFocus === 'function') {
        trapFocus(outer, summary);
      } else {
        summary.focus({ preventScroll: true });
      }
    }
  }

  function openSubmenu(details) {
    if (!details) return;
    const main = details.closest('.mobile-facets__main');
    main?.querySelectorAll(':scope > .mobile-facets__details[open]')
      .forEach((other) => { if (other !== details) closeSubmenu(other, false); });

    details.open = true;
    details.classList.add('menu-opening');
    main?.classList.add('submenu-open');
    details.querySelector(':scope > .mobile-facets__summary')
      ?.setAttribute('aria-expanded', 'true');

    // Keep focus trapped in the currently visible filter choices.
    const submenu = details.querySelector(':scope > .mobile-facets__submenu');
    const backButton = submenu?.querySelector('.mobile-facets__close-button');
    if (submenu && backButton && typeof trapFocus === 'function') {
      requestAnimationFrame(() => {
        if (details.open && details.classList.contains('menu-opening')) {
          trapFocus(submenu, backButton);
        }
      });
    }
  }

  // Capture *before* Refresh's menu-drawer handlers, which otherwise
  // delay submenu opening by 100ms and closing by 400ms.
  document.addEventListener('click', (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const main = event.target.closest(scope);
    if (!main) return;

    const back = event.target.closest('.mobile-facets__close-button');
    if (back && main.contains(back)) {
      const details = back.closest('.mobile-facets__details');
      if (!details) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeSubmenu(details);
      return;
    }

    const summary = event.target.closest('.mobile-facets__summary');
    if (!summary || !main.contains(summary)) return;
    const details = summary.closest('.mobile-facets__details');
    if (!details || !main.contains(details)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (details.open) closeSubmenu(details);
    else openSubmenu(details);
  }, true);

  // Escape should also close immediately, without the old drawer delay.
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' ||
        !window.matchMedia('(max-width: 749px)').matches) return;
    const details = event.target.closest(`${scope} .mobile-facets__details[open]`);
    if (!details) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeSubmenu(details);
  }, true);
})();

/* FF | Prevent mobile Sort scroll from moving the underlying page.
   Touches starting in the options list scroll that list; gestures
   outside it and overscroll at its ends are blocked. */
(() => {
  const mobile = () => window.matchMedia('(max-width: 749px)').matches;
  const activeList = () => mobile()
    ? document.querySelector('#MainContent .ff-sort-details[open] .ff-sort-menu')
    : null;
  let touchStartY = 0;
  let touchStartedInList = false;

  const atEdge = (list, delta) => {
    const limit = Math.max(0, list.scrollHeight - list.clientHeight);
    if (limit <= 1) return true;
    // delta > 0 means the finger travels down / scrolling toward top.
    return (delta > 0 && list.scrollTop <= 1) ||
           (delta < 0 && list.scrollTop >= limit - 1);
  };

  document.addEventListener('touchstart', (event) => {
    const list = activeList();
    if (!list || event.touches.length !== 1) return;
    touchStartY = event.touches[0].clientY;
    touchStartedInList = list.contains(event.target);
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', (event) => {
    const list = activeList();
    if (!list || !event.cancelable || event.touches.length !== 1) return;
    if (!touchStartedInList) {
      event.preventDefault();
      return;
    }
    const currentY = event.touches[0].clientY;
    const delta = currentY - touchStartY;
    if (atEdge(list, delta)) event.preventDefault();
    else touchStartY = currentY;
  }, { capture: true, passive: false });

  document.addEventListener('touchend', () => { touchStartedInList = false; }, { passive: true });
  document.addEventListener('touchcancel', () => { touchStartedInList = false; }, { passive: true });

  // Trackpads / mouse wheels on mobile-width browser windows.
  document.addEventListener('wheel', (event) => {
    const list = activeList();
    if (!list || !event.cancelable) return;
    if (!list.contains(event.target) || atEdge(list, -event.deltaY)) {
      event.preventDefault();
    }
  }, { capture: true, passive: false });
})();
